import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import colors from '../theme/colors';
import { apiService, resolveStrapiMediaUrl } from '../services/api';

interface MenuItem {
  id?: number;
  label: string;
  linkType?: 'page' | 'article' | 'custom';
  page?: {
    slug?: string;
    id?: number;
    data?: {
      slug?: string;
    };
  } | string | null;
  article?: {
    slug?: string;
    id?: number;
    data?: {
      slug?: string;
    };
  } | string | null;
  url?: string;
  order?: number;
}

// Helper function to extract slug from page/article object
const extractSlug = (obj: any): string | null => {
  if (!obj) return null;
  if (typeof obj === 'string') return obj;
  if (typeof obj !== 'object') return null;

  if ('slug' in obj && obj.slug) return obj.slug;
  if ('data' in obj && obj.data) {
    const data = obj.data;
    if (typeof data === 'object' && data !== null) {
      if ('slug' in data && data.slug) return data.slug;
      if (Array.isArray(data) && data.length > 0 && data[0].slug) return data[0].slug;
    }
  }

  return null;
};

// Helper function to get URL from menu item
const getMenuItemUrl = (item: MenuItem): string => {
  if (item.linkType === 'page' && item.page) {
    const pageSlug = extractSlug(item.page);
    if (pageSlug) return `/page/${pageSlug}`;
    return '/';
  }

  if (item.linkType === 'article' && item.article) {
    const articleSlug = extractSlug(item.article);
    if (articleSlug) return `/article/${articleSlug}`;
    return '/';
  }

  if (item.linkType === 'custom' && item.url) {
    return item.url;
  }

  if (!item.linkType && item.url) {
    return item.url;
  }

  return '/';
};

const isExternalNavUrl = (url: string): boolean =>
  /^https?:\/\//i.test(url) ||
  url.startsWith('//') ||
  /^mailto:/i.test(url) ||
  /^tel:/i.test(url);

const HeaderContainer = styled.header`
  background-color: ${colors.mainBlack};
  color: ${colors.mainWhite};
  padding: 1rem 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 1rem 2rem;

  @media (max-width: 640px) {
    grid-template-columns: auto 1fr auto;
    gap: 0.75rem 1rem;
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  justify-self: start;
  min-width: 0;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: ${colors.mainWhite};
`;

const LogoImage = styled.img`
  height: 40px;
  width: auto;
`;

const Nav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 2rem;
  align-items: center;
  justify-content: center;
  justify-self: center;
`;

const navItemCss = `
  color: ${colors.mainWhite};
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
  position: relative;

  &:hover {
    color: ${colors.mainOrange};
  }
`;

const NavLink = styled(Link)<{ $isActive?: boolean }>`
  ${navItemCss}

  &.active {
    color: ${colors.mainOrange};

    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      right: 0;
      height: 2px;
      background-color: ${colors.mainOrange};
    }
  }
`;

/** Зовнішні посилання (https://, mailto:, tel:) — не через React Router */
const ExternalNavLink = styled.a`
  ${navItemCss}
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-shrink: 0;
  justify-self: end;
`;

const SearchIconLink = styled(Link)`
  display: flex;
  align-items: center;
  padding: 0.5rem;
  color: ${colors.mainWhite};
  transition: color 0.3s ease;

  &:hover {
    color: ${colors.mainOrange};
  }
`;

const Header: React.FC = () => {
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [logoAlt, setLogoAlt] = useState<string>('Logo');
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const location = useLocation();

  useEffect(() => {
    const fetchHeaderSettings = async () => {
      try {
        const headerSettings = await apiService.getHeaderSettings();

        if (!headerSettings) {
          return;
        }

        // Extract logo
        if (headerSettings.logo) {
          const logo = headerSettings.logo;
          let logoUrlValue: string | null = null;

          if (typeof logo === 'string') {
            logoUrlValue = logo;
          } else if (typeof logo === 'object' && logo !== null) {
            if ('url' in logo && logo.url) {
              logoUrlValue = logo.url;
            } else if ('data' in logo && logo.data) {
              const logoData = logo.data;
              if (typeof logoData === 'object' && logoData !== null && 'url' in logoData) {
                logoUrlValue = logoData.url;
              } else if (Array.isArray(logoData) && logoData.length > 0 && logoData[0].url) {
                logoUrlValue = logoData[0].url;
              }
            } else if ('formats' in logo && logo.formats) {
              const formats = logo.formats;
              if (formats.large?.url) logoUrlValue = formats.large.url;
              else if (formats.medium?.url) logoUrlValue = formats.medium.url;
              else if (formats.small?.url) logoUrlValue = formats.small.url;
              else if (formats.thumbnail?.url) logoUrlValue = formats.thumbnail.url;
            }
          }

          if (logoUrlValue) {
            setLogoUrl(resolveStrapiMediaUrl(logoUrlValue));
          }
        }

        if (headerSettings.logoAlt) {
          setLogoAlt(headerSettings.logoAlt);
        }

        // Extract navigation
        if (headerSettings.navigation) {
          let navigationItems: MenuItem[] = [];

          if (headerSettings.navigation.items && Array.isArray(headerSettings.navigation.items)) {
            navigationItems = headerSettings.navigation.items;
          } else {
            const navId = headerSettings.navigation.id?.toString();
            const navSlug = headerSettings.navigation.slug || headerSettings.navigation.documentId;

            if (navId || navSlug) {
              try {
                const navResponse = await apiService.getNavigation(navId || navSlug || '');
                if (navResponse && Array.isArray(navResponse) && navResponse.length > 0) {
                  navigationItems = navResponse;
                }
              } catch {
                // Navigation fetch failed
              }
            }
          }

          const sorted = [...navigationItems].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          setMenuItems(sorted);
        }
      } catch (error) {
        // Error fetching header settings
      }
    };

    fetchHeaderSettings();
  }, []);

  return (
    <HeaderContainer>
      <HeaderContent>
        <LogoSection>
          <Logo to="/">
            {logoUrl && (
              <LogoImage
                src={logoUrl}
                alt={logoAlt}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
          </Logo>
        </LogoSection>

        <Nav>
          {menuItems.map((item) => {
            const url = getMenuItemUrl(item);
            const key = item.id || url;

            if (isExternalNavUrl(url)) {
              return (
                <ExternalNavLink key={key} href={url}>
                  {item.label}
                </ExternalNavLink>
              );
            }

            return (
              <NavLink
                key={key}
                to={url}
                className={location.pathname === url ? 'active' : ''}
              >
                {item.label}
              </NavLink>
            );
          })}
        </Nav>

        <RightSection>
          <SearchIconLink to="/search" aria-label="Пошук">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
          </SearchIconLink>
        </RightSection>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;
